const WISHLIST_LEGACY_STORAGE_KEY = 'm-wishlist-products'

class Wishlist {
  constructor() {
    // Prevent multiple instances
    if (window.WishlistInstance) return window.WishlistInstance;

    this.isWishlistPage = false
    this.products = []
    this.productNodes = {}
    this.isRendering = false;

    // customerId comes from snippets/theme-data.liquid and is null for guests.
    this.customerId = (window.MinimogSettings && MinimogSettings.customerId) || null
    this.isLoggedIn = !!this.customerId

    // CLIENT REQUEST: guests can save favorites without an account.
    // Logged-in shoppers get a key scoped to their customer id (so two people on
    // one device never inherit each other's list). Guests fall back to one shared
    // anonymous key in this browser — the same key that used to be the only key
    // before per-customer storage existed. This lets us reuse migrateLegacyStorage()
    // below to move a guest's list into their account the moment they log in.
    this.storageKey = this.isLoggedIn
      ? `${WISHLIST_LEGACY_STORAGE_KEY}--${this.customerId}`
      : WISHLIST_LEGACY_STORAGE_KEY

    this.pageTemplate = 'page.wishlist'
    this.addedClass = 'added-to-wishlist'
    this.hasItemClass = 'wishlist-has-item'
    this.selectors = {
      root: '.m-wishlist-page',
      container: '.m-wishlist-page-content__wrapper',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count',
      loginPrompt: '#m-wishlist-login-prompt'
    }

    if (this.isLoggedIn) {
      this.migrateLegacyStorage()
    }
    this.products = this.readStorage(this.storageKey)

    this.isWishlistPage = MinimogSettings.template === this.pageTemplate
    this.init()

    window.WishlistInstance = this;
  }

  readStorage = (key) => {
    if (!key) return []
    try {
      const stored = JSON.parse(localStorage.getItem(key))
      if (!Array.isArray(stored)) return []
      return Array.from(new Set(stored.filter(h => h && typeof h === 'string')))
    } catch (e) {
      return []
    }
  }

  /**
   * Carries a guest's anonymous-key wishlist over to the customer-scoped key the
   * moment they sign in, so favorites saved before login aren't lost. Runs only
   * for logged-in sessions; if this customer already has their own saved list we
   * leave it untouched and just clear the anonymous key.
   */
  migrateLegacyStorage = () => {
    try {
      if (localStorage.getItem(this.storageKey) !== null) {
        localStorage.removeItem(WISHLIST_LEGACY_STORAGE_KEY)
        return
      }
      const legacy = this.readStorage(WISHLIST_LEGACY_STORAGE_KEY)
      if (legacy.length) {
        localStorage.setItem(this.storageKey, JSON.stringify(legacy))
      }
      localStorage.removeItem(WISHLIST_LEGACY_STORAGE_KEY)
    } catch (e) {
      /* storage unavailable (private mode / quota) — nothing to migrate */
    }
  }

  init = async () => {
    // Guests now have a real (anonymous-key) list too, so the wishlist page
    // renders for everyone, not just signed-in customers.
    if (this.isWishlistPage) {
      await this.renderWishlistPage()
      this.addEventToRemoveButtons()
    }
    this.setWishlistButtonsState()
    this.addEventToWishlistButtons()
    this.updateWishlistCount()
  }

  saveToStorage = () => {
    this.products = Array.from(new Set(this.products.filter(h => h && typeof h === 'string')))
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.products))
    } catch (e) {
      /* storage unavailable (private mode / quota) — in-memory state still updates */
    }
    this.updateWishlistCount()
  }

  addToWishlist(handle) {
    if (handle && !this.products.includes(handle)) {
      this.products.push(handle)
      this.saveToStorage()
    }
  }

  /**
   * Kept as a fallback only — no longer called on a normal wishlist click since
   * guests can now save favorites without an account. Left in place in case some
   * other flow (e.g. a future login-gated feature) still needs it.
   */
  promptLogin = () => {
    const dialog = document.querySelector(this.selectors.loginPrompt)

    if (!dialog || typeof dialog.showModal !== 'function') {
      const url = window.MinimogSettings && MinimogSettings.wishlistLoginUrl
      if (url) window.location.href = url
      return
    }

    if (!dialog.dataset.wishlistBound) {
      dialog.dataset.wishlistBound = 'true'
      dialog.querySelectorAll('[data-wishlist-login-close]').forEach(btn => {
        btn.addEventListener('click', () => dialog.close())
      })
      // Click on the backdrop (the dialog element itself, outside its inner box).
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) dialog.close()
      })
    }

    if (!dialog.open) dialog.showModal()
  }

  removeFromWishlist(handle) {
    if (!handle) return;
    this.products = this.products.filter(hdl => hdl !== handle)
    this.saveToStorage()
  }

  updateButtonUI = (btn, isInWishlist) => {
    if (!btn) return;
    if (isInWishlist) {
      btn.classList.add(this.addedClass);
    } else {
      btn.classList.remove(this.addedClass);
    }
  }

  syncAllButtonsWithState = (handle, isAdded) => {
    const selector = `[data-product-handle="${handle}"]`;
    document.querySelectorAll(selector).forEach(btn => {
      this.updateButtonUI(btn, isAdded);
    });
  }

  setWishlistButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton + ', ' + this.selectors.removeButton)
    buttons.forEach(btn => {
      const prodHandle = btn.dataset.productHandle
      this.updateButtonUI(btn, this.products.includes(prodHandle));
    })
  }

  updateWishlistCount = () => {
    // Guests now have a real list too, so the header badge reflects it just
    // like it does for logged-in customers.
    const size = this.products.length
    const countElems = document.querySelectorAll(this.selectors.count);
    [...countElems].forEach(elem => {
      elem.textContent = size
      if (size < 1) {
        elem.classList.add('m:hidden')
      } else {
        elem.classList.remove('m:hidden')
      }
    })
    const method = size ? 'add' : 'remove'
    if (document.body) document.body.classList[method](this.hasItemClass)
  }

  addEventToWishlistButtons = () => {
    if (typeof addEventDelegate !== 'function') return;

    addEventDelegate({
      selector: this.selectors.wishlistButton,
      handler: (e, btn) => {
        e.preventDefault()
        e.stopPropagation()

        const productHandle = btn.dataset.productHandle
        if (!productHandle) return;

        const isCurrentlyInWishlist = this.products.includes(productHandle);
        const newState = !isCurrentlyInWishlist;

        if (newState) {
          this.addToWishlist(productHandle)
          this.syncAllButtonsWithState(productHandle, true)
        } else {
          this.removeFromWishlist(productHandle)
          this.syncAllButtonsWithState(productHandle, false)
          if (this.isWishlistPage) this.handleCardRemoval(btn)
        }
      }
    })
  }

  addEventToRemoveButtons = () => {
    if (typeof addEventDelegate !== 'function') return;
    
    addEventDelegate({
      selector: this.selectors.removeButton,
      handler: (e, btn) => {
        e.preventDefault()
        e.stopPropagation()

        const productHandle = btn.dataset.productHandle
        if (!productHandle) return;

        this.removeFromWishlist(productHandle)
        this.syncAllButtonsWithState(productHandle, false)
        this.handleCardRemoval(btn)
      }
    })
  }

  handleCardRemoval = (btn) => {
    const cardWrapper = btn.closest(this.selectors.wrapper)
    if (cardWrapper) {
      // Improved exit animation
      cardWrapper.style.opacity = '0'
      cardWrapper.style.transform = 'scale(0.8) translateY(20px)'
      cardWrapper.style.pointerEvents = 'none'
      
      setTimeout(() => {
        cardWrapper.remove()
        if (this.products.length === 0) {
          this.showNoProductsMessage()
        }
      }, 400)
    }
  }

  // Enhanced "X" button design
  wishlistRemoveButton(prdHandle) {
    const button = document.createElement("DIV");
    button.classList.add("m-wishlist-remove-button");
    button.classList.add("animate--fade-in-up");
    button.setAttribute("data-product-handle", prdHandle);
    
    // Inline styling for immediate premium look
    Object.assign(button.style, {
      position: 'absolute',
      top: '8px',
      right: '22px',
      width: '32px',
      height: '32px',
      backgroundColor: '#fff',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
      cursor: 'pointer',
      zIndex: '10',
      transition: 'all 0.2s ease'
    });

    // Hover effect via JS
    button.onmouseover = () => { button.style.backgroundColor = '#f3f3f3'; button.style.transform = 'scale(1.1)'; };
    button.onmouseout = () => { button.style.backgroundColor = '#fff'; button.style.transform = 'scale(1)'; };

    // Label comes from the locale files via theme-data.liquid — it used to be a
    // hardcoded English string that never translated.
    const removeLabel = (window.MinimogSettings && MinimogSettings.wishlistRemoveLabel) || 'Remove'
    button.setAttribute('role', 'button')
    button.setAttribute('tabindex', '0')
    button.setAttribute('aria-label', removeLabel)

    button.innerHTML = `
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        <path d="M13 1L1 13M1 1L13 13" stroke="#d50405" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="m-tooltip__content" style="font-size: 11px;"></span>
    `;
    button.querySelector('.m-tooltip__content').textContent = removeLabel;

    return button;
  }

  renderWishlistPage = async () => {
    if (this.isRendering) return;
    this.isRendering = true;

    const container = document.querySelector(this.selectors.container)
    if (!container) {
      this.isRendering = false;
      return;
    }

    if (this.products.length === 0) {
      this.showNoProductsMessage()
      this.isRendering = false;
      return
    }

    const validHandles = this.products.filter(h => h && typeof h === 'string');
    const fragment = document.createDocumentFragment();
    let validItemCount = 0;
    // Handles that no longer resolve to a product (deleted, unpublished, handle
    // changed). Previously these were left in storage forever, so the page showed
    // "no favorites" while the header badge kept counting them.
    const deadHandles = [];

    const promises = validHandles.map(async (hdl) => {
      try {
        if (typeof formatUrl !== 'function' || typeof fetchCache !== 'function') return;

        const url = formatUrl('products', hdl, 'view=grid-card-item')
        const prodHTML = await fetchCache(url)

        if (!prodHTML || prodHTML.trim() === "") {
          deadHandles.push(hdl);
          return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(prodHTML, 'text/html');
        const productCardNode = doc.querySelector(this.selectors.productCard);

        if (!productCardNode) {
          deadHandles.push(hdl);
        }

        if (productCardNode) {
          const item = document.createElement("DIV")
          item.classList.add("m-wishlist-card", "m:column")
          
          // Ensure smooth animations
          Object.assign(item.style, {
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            opacity: '1',
            transform: 'scale(1)'
          });
          
          item.appendChild(productCardNode.cloneNode(true));
          item.appendChild(this.wishlistRemoveButton(hdl))
          
          this.productNodes[hdl] = item
          validItemCount++;
        }
      } catch (error) {
        console.error(`Error loading product: ${hdl}`, error)
      }
    })

    await Promise.all(promises)

    // Drop handles that no longer resolve, so storage, the badge and the page
    // agree with each other on the next load.
    if (deadHandles.length) {
      this.products = this.products.filter(hdl => !deadHandles.includes(hdl))
      this.saveToStorage()
    }

    if (validItemCount > 0) {
      container.innerHTML = '';
      validHandles.forEach(hdl => {
        if (this.productNodes[hdl]) fragment.appendChild(this.productNodes[hdl]);
      });
      container.appendChild(fragment);

      if (MinimogTheme.CompareProduct) MinimogTheme.CompareProduct.setCompareButtonsState();

      this.setWishlistButtonsState()
      container.classList.add('is-visible')
      this.setPageState('has-items')
    } else {
      this.showNoProductsMessage()
    }

    this.isRendering = false;
  }

  /**
   * Single switch for which of the page's three panels is visible. The initial
   * value is written synchronously by the inline script in
   * sections/page-wishlist.liquid so nothing flashes before this runs.
   */
  setPageState = (state) => {
    const root = document.querySelector(this.selectors.root)
    if (root) root.setAttribute('data-wishlist-state', state)
  }

  showNoProductsMessage = () => {
    this.setPageState('empty')
  }
}

// Global initialization
if (!MinimogTheme.Wishlist) {
    MinimogTheme.Wishlist = new Wishlist()
}